import { __decorate, __extends } from "tslib";
import { NodeMaterialBlock } from "../../nodeMaterialBlock.js";
import { NodeMaterialBlockConnectionPointTypes } from "../../Enums/nodeMaterialBlockConnectionPointTypes.js";
import { NodeMaterialBlockTargets } from "../../Enums/nodeMaterialBlockTargets.js";
import { NodeMaterialConnectionPointDirection } from "../../nodeMaterialBlockConnectionPoint.js";
import { RegisterClass } from "../../../../Misc/typeStore.js";
import { InputBlock } from "../Input/inputBlock.js";
import { editableInPropertyPage, PropertyTypeForEdition } from "../../nodeMaterialDecorator.js";
import { NodeMaterialConnectionPointCustomObject } from "../../nodeMaterialConnectionPointCustomObject.js";
import { TBNBlock } from "./TBNBlock.js";
import "../../../../Shaders/ShadersInclude/bumpFragmentMainFunctions.js";
import "../../../../Shaders/ShadersInclude/bumpFragmentFunctions.js";
import "../../../../Shaders/ShadersInclude/bumpFragment.js";
/**
 * Block used to perturb normals based on a normal map
 */
var PerturbNormalBlock = /** @class */ (function (_super) {
    __extends(PerturbNormalBlock, _super);
    /**
     * Create a new PerturbNormalBlock
     * @param name defines the block name
     */
    function PerturbNormalBlock(name) {
        var _this = _super.call(this, name, NodeMaterialBlockTargets.Fragment) || this;
        _this._tangentSpaceParameterName = "";
        /** Gets or sets a boolean indicating that normal should be inverted on X axis */
        _this.invertX = false;
        /** Gets or sets a boolean indicating that normal should be inverted on Y axis */
        _this.invertY = false;
        /** Gets or sets a boolean indicating that parallax occlusion should be enabled */
        _this.useParallaxOcclusion = false;
        _this._isUnique = true;
        // Vertex
        _this.registerInput("worldPosition", NodeMaterialBlockConnectionPointTypes.Vector4, false);
        _this.registerInput("worldNormal", NodeMaterialBlockConnectionPointTypes.Vector4, false);
        _this.registerInput("worldTangent", NodeMaterialBlockConnectionPointTypes.Vector4, true);
        _this.registerInput("uv", NodeMaterialBlockConnectionPointTypes.Vector2, false);
        _this.registerInput("normalMapColor", NodeMaterialBlockConnectionPointTypes.Color3, false);
        _this.registerInput("strength", NodeMaterialBlockConnectionPointTypes.Float, false);
        _this.registerInput("viewDirection", NodeMaterialBlockConnectionPointTypes.Vector3, true);
        _this.registerInput("parallaxScale", NodeMaterialBlockConnectionPointTypes.Float, true);
        _this.registerInput("parallaxHeight", NodeMaterialBlockConnectionPointTypes.Float, true);
        _this.registerInput("TBN", NodeMaterialBlockConnectionPointTypes.Object, true, NodeMaterialBlockTargets.VertexAndFragment, new NodeMaterialConnectionPointCustomObject("TBN", _this, NodeMaterialConnectionPointDirection.Input, TBNBlock, "TBNBlock"));
        // Fragment
        _this.registerOutput("output", NodeMaterialBlockConnectionPointTypes.Vector4);
        _this.registerOutput("uvOffset", NodeMaterialBlockConnectionPointTypes.Vector2);
        return _this;
    }
    /**
     * Gets the current class name
     * @returns the class name
     */
    PerturbNormalBlock.prototype.getClassName = function () {
        return "PerturbNormalBlock";
    };
    Object.defineProperty(PerturbNormalBlock.prototype, "worldPosition", {
        /**
         * Gets the world position input component
         */
        get: function () {
            return this._inputs[0];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "worldNormal", {
        /**
         * Gets the world normal input component
         */
        get: function () {
            return this._inputs[1];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "worldTangent", {
        /**
         * Gets the world tangent input component
         */
        get: function () {
            return this._inputs[2];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "uv", {
        /**
         * Gets the uv input component
         */
        get: function () {
            return this._inputs[3];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "normalMapColor", {
        /**
         * Gets the normal map color input component
         */
        get: function () {
            return this._inputs[4];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "strength", {
        /**
         * Gets the strength input component
         */
        get: function () {
            return this._inputs[5];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "viewDirection", {
        /**
         * Gets the view direction input component
         */
        get: function () {
            return this._inputs[6];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "parallaxScale", {
        /**
         * Gets the parallax scale input component
         */
        get: function () {
            return this._inputs[7];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "parallaxHeight", {
        /**
         * Gets the parallax height input component
         */
        get: function () {
            return this._inputs[8];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "TBN", {
        /**
         * Gets the TBN input component
         */
        // eslint-disable-next-line @typescript-eslint/naming-convention
        get: function () {
            return this._inputs[9];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "output", {
        /**
         * Gets the output component
         */
        get: function () {
            return this._outputs[0];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerturbNormalBlock.prototype, "uvOffset", {
        /**
         * Gets the uv offset output component
         */
        get: function () {
            return this._outputs[1];
        },
        enumerable: false,
        configurable: true
    });
    PerturbNormalBlock.prototype.prepareDefines = function (mesh, nodeMaterial, defines) {
        var normalSamplerName = this.normalMapColor.connectedPoint._ownerBlock.samplerName;
        var useParallax = this.viewDirection.isConnected && ((this.useParallaxOcclusion && normalSamplerName) || (!this.useParallaxOcclusion && this.parallaxHeight.isConnected));
        defines.setValue("BUMP", true);
        defines.setValue("PARALLAX", useParallax, true);
        defines.setValue("PARALLAXOCCLUSION", this.useParallaxOcclusion, true);
    };
    PerturbNormalBlock.prototype.bind = function (effect, nodeMaterial) {
        if (nodeMaterial.getScene()._mirroredCameraPosition) {
            effect.setFloat2(this._tangentSpaceParameterName, this.invertX ? 1.0 : -1.0, this.invertY ? 1.0 : -1.0);
        }
        else {
            effect.setFloat2(this._tangentSpaceParameterName, this.invertX ? -1.0 : 1.0, this.invertY ? -1.0 : 1.0);
        }
    };
    PerturbNormalBlock.prototype.autoConfigure = function (material) {
        if (!this.uv.isConnected) {
            var uvInput = material.getInputBlockByPredicate(function (b) { return b.isAttribute && b.name === "uv"; });
            if (!uvInput) {
                uvInput = new InputBlock("uv");
                uvInput.setAsAttribute();
            }
            uvInput.output.connectTo(this.uv);
        }
        if (!this.strength.isConnected) {
            var strengthInput = new InputBlock("strength");
            strengthInput.value = 1.0;
            strengthInput.output.connectTo(this.strength);
        }
    };
    PerturbNormalBlock.prototype._buildBlock = function (state) {
        _super.prototype._buildBlock.call(this, state);
        var comments = "//".concat(this.name);
        var uv = this.uv;
        var worldPosition = this.worldPosition;
        var worldNormal = this.worldNormal;
        var worldTangent = this.worldTangent;
        state.sharedData.blocksWithDefines.push(this);
        state.sharedData.bindableBlocks.push(this);
        this._tangentSpaceParameterName = state._getFreeDefineName("tangentSpaceParameter");
        state._emitUniformFromString(this._tangentSpaceParameterName, "vec2");
        var normalSamplerName = this.normalMapColor.connectedPoint._ownerBlock.samplerName;
        var useParallax = this.viewDirection.isConnected && ((this.useParallaxOcclusion && normalSamplerName) || (!this.useParallaxOcclusion && this.parallaxHeight.isConnected));
        var replaceForParallaxInfos = !this.parallaxScale.isConnectedToInputBlock
            ? "0.05"
            : this.parallaxScale.connectInputBlock.isConstant
                ? state._emitFloat(this.parallaxScale.connectInputBlock.value)
                : this.parallaxScale.associatedVariableName;
        var replaceForBumpInfos = this.strength.isConnectedToInputBlock && this.strength.connectInputBlock.isConstant
            ? "\r\n#if !defined(NORMALXYSCALE)\r\n1.0/\r\n#endif\r\n".concat(state._emitFloat(this.strength.connectInputBlock.value))
            : "\r\n#if !defined(NORMALXYSCALE)\r\n1.0/\r\n#endif\r\n".concat(this.strength.associatedVariableName);
        state._emitExtension("derivatives", "#extension GL_OES_standard_derivatives : enable");
        var tangentReplaceString = { search: /defined\(TANGENT\)/g, replace: worldTangent.isConnected ? "defined(TANGENT)" : "defined(IGNORE)" };
        var tbnVarying = { search: /varying mat3 vTBN/g, replace: "" };
        var TBN = this.TBN;
        if (TBN.isConnected) {
            state.compilationString += "\n            #ifdef TBNBLOCK\n            mat3 vTBN = ".concat(TBN.associatedVariableName, ";\n            #endif\n            ");
        }
        else if (worldTangent.isConnected) {
            state.compilationString += "vec3 tbnNormal = normalize(".concat(worldNormal.associatedVariableName, ".xyz);\r\n");
            state.compilationString += "vec3 tbnTangent = normalize(".concat(worldTangent.associatedVariableName, ".xyz);\r\n");
            state.compilationString += "vec3 tbnBitangent = cross(tbnNormal, tbnTangent);\r\n";
            state.compilationString += "mat3 vTBN = mat3(tbnTangent, tbnBitangent, tbnNormal);\r\n";
        }
        state._emitFunctionFromInclude("bumpFragmentMainFunctions", comments, {
            replaceStrings: [tangentReplaceString, tbnVarying],
        });
        state._emitFunctionFromInclude("bumpFragmentFunctions", comments, {
            replaceStrings: [
                { search: /#include<samplerFragmentDeclaration>\(_DEFINENAME_,BUMP,_VARYINGNAME_,Bump,_SAMPLERNAME_,bump\)/g, replace: "" },
                { search: /uniform sampler2D bumpSampler;/g, replace: "" },
                {
                    search: /vec2 parallaxOcclusion\(vec3 vViewDirCoT,vec3 vNormalCoT,vec2 texCoord,float parallaxScale\)/g,
                    replace: "#define inline\r\nvec2 parallaxOcclusion(vec3 vViewDirCoT, vec3 vNormalCoT, vec2 texCoord, float parallaxScale, sampler2D bumpSampler)",
                },
                { search: /vec2 parallaxOffset\(vec3 viewDir,float heightScale\)/g, replace: "vec2 parallaxOffset(vec3 viewDir, float heightScale, float height_)" },
                { search: /texture2D\(bumpSampler,vBumpUV\)\.w/g, replace: "height_" },
            ],
        });
        var uvForPerturbNormal = !useParallax || !normalSamplerName ? this.normalMapColor.associatedVariableName : "texture2D(".concat(normalSamplerName, ", ").concat(uv.associatedVariableName, " + uvOffset).xyz");
        state.compilationString += this._declareOutput(this.output, state) + " = vec4(0.);\r\n";
        state.compilationString += state._emitCodeFromInclude("bumpFragment", comments, {
            replaceStrings: [
                { search: /perturbNormal\(TBN,texture2D\(bumpSampler,vBumpUV\+uvOffset\).xyz,vBumpInfos.y\)/g, replace: "perturbNormal(TBN, ".concat(uvForPerturbNormal, ", vBumpInfos.y)") },
                {
                    search: /parallaxOcclusion\(invTBN\*-viewDirectionW,invTBN\*normalW,vBumpUV,vBumpInfos.z\)/g,
                    replace: "parallaxOcclusion((invTBN * -viewDirectionW), (invTBN * normalW), vBumpUV, vBumpInfos.z, ".concat(useParallax && this.useParallaxOcclusion ? normalSamplerName : "bumpSampler", ")"),
                },
                {
                    search: /parallaxOffset\(invTBN\*viewDirectionW,vBumpInfos\.z\)/g,
                    replace: "parallaxOffset(invTBN * viewDirectionW, vBumpInfos.z, ".concat(useParallax ? this.parallaxHeight.associatedVariableName : "0.", ")"),
                },
                { search: /vTangentSpaceParams/g, replace: this._tangentSpaceParameterName },
                { search: /vBumpInfos.y/g, replace: replaceForBumpInfos },
                { search: /vBumpInfos.z/g, replace: replaceForParallaxInfos },
                { search: /vBumpUV/g, replace: uv.associatedVariableName },
                { search: /vPositionW/g, replace: worldPosition.associatedVariableName + ".xyz" },
                { search: /normalW=/g, replace: this.output.associatedVariableName + ".xyz = " },
                { search: /mat3\(normalMatrix\)\*normalW/g, replace: "mat3(normalMatrix) * " + this.output.associatedVariableName + ".xyz" },
                { search: /normalW/g, replace: worldNormal.associatedVariableName + ".xyz" },
                { search: /viewDirectionW/g, replace: useParallax ? this.viewDirection.associatedVariableName : "vec3(0.)" },
                tangentReplaceString,
            ],
        });
        return this;
    };
    PerturbNormalBlock.prototype._dumpPropertiesCode = function () {
        var codeString = _super.prototype._dumpPropertiesCode.call(this) + "".concat(this._codeVariableName, ".invertX = ").concat(this.invertX, ";\r\n");
        codeString += "".concat(this._codeVariableName, ".invertY = ").concat(this.invertY, ";\r\n");
        codeString += "".concat(this._codeVariableName, ".useParallaxOcclusion = ").concat(this.useParallaxOcclusion, ";\r\n");
        return codeString;
    };
    PerturbNormalBlock.prototype.serialize = function () {
        var serializationObject = _super.prototype.serialize.call(this);
        serializationObject.invertX = this.invertX;
        serializationObject.invertY = this.invertY;
        serializationObject.useParallaxOcclusion = this.useParallaxOcclusion;
        return serializationObject;
    };
    PerturbNormalBlock.prototype._deserialize = function (serializationObject, scene, rootUrl) {
        _super.prototype._deserialize.call(this, serializationObject, scene, rootUrl);
        this.invertX = serializationObject.invertX;
        this.invertY = serializationObject.invertY;
        this.useParallaxOcclusion = !!serializationObject.useParallaxOcclusion;
    };
    __decorate([
        editableInPropertyPage("Invert X axis", PropertyTypeForEdition.Boolean, "PROPERTIES", { notifiers: { update: false } })
    ], PerturbNormalBlock.prototype, "invertX", void 0);
    __decorate([
        editableInPropertyPage("Invert Y axis", PropertyTypeForEdition.Boolean, "PROPERTIES", { notifiers: { update: false } })
    ], PerturbNormalBlock.prototype, "invertY", void 0);
    __decorate([
        editableInPropertyPage("Use parallax occlusion", PropertyTypeForEdition.Boolean)
    ], PerturbNormalBlock.prototype, "useParallaxOcclusion", void 0);
    return PerturbNormalBlock;
}(NodeMaterialBlock));
export { PerturbNormalBlock };
RegisterClass("BABYLON.PerturbNormalBlock", PerturbNormalBlock);
//# sourceMappingURL=perturbNormalBlock.js.map